import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "../../lib/auth-context";
import { needsTermsAcceptance } from "../../lib/terms";
import { LoadingSpinner } from "./LoadingSpinner";

type TermsGuardProps = {
	children: React.ReactNode;
};

/**
 * TermsGuard wraps protected pro routes.
 * It redirects users to /accept-terms if they haven't accepted the current terms.
 * This guard should be used AFTER AuthGuard and BEFORE OnboardingGuard.
 */
export function TermsGuard({ children }: TermsGuardProps) {
	const { user, isAuthenticated, isLoading } = useAuth();
	const navigate = useNavigate();

	const termsRequired =
		isAuthenticated && needsTermsAcceptance(user?.termsVersion ?? null);

	useEffect(() => {
		if (!isLoading && termsRequired) {
			navigate({ to: "/accept-terms" });
		}
	}, [isLoading, termsRequired, navigate]);

	if (isLoading) {
		return <LoadingSpinner />;
	}

	if (termsRequired) {
		return <LoadingSpinner />; // Show spinner while redirecting
	}

	return <>{children}</>;
}
